import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Clock, Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { CONTACT, SOCIAL } from '../constants/site';
import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, MaskedTitle, Overline, EASE } from '../components/motion';
import { useSEO } from '../components/SEO';
import { Testimonials } from '../components/Testimonials';
import { LeadForm } from '../components/LeadForm';
import { WhatsAppIcon } from '../components/WhatsAppFloat';

// Rolling-text hover: value slides up, accent duplicate rolls in from below
const RollText = ({ text, className = '' }) => (
  <span className={`relative block overflow-hidden ${className}`}>
    <span className="block transition-transform duration-300 ease-out group-hover/roll:-translate-y-full">{text}</span>
    <span aria-hidden="true" className="absolute inset-0 block translate-y-full transition-transform duration-300 ease-out group-hover/roll:translate-y-0 text-accent">
      {text}
    </span>
  </span>
);

// Magnetic external-link pill with sheen sweep (WhatsApp green etc.)
const MagneticLink = ({ href, testId, className = '', children }) => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 10,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 8,
    });
  };
  return (
    <motion.div
      animate={{ x: offset.x, y: offset.y }}
      transition={{ type: 'spring', stiffness: 180, damping: 14, mass: 0.6 }}
    >
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        data-testid={testId}
        onMouseMove={onMove}
        onMouseLeave={() => setOffset({ x: 0, y: 0 })}
        className={`group/mag relative flex items-center justify-center gap-3 overflow-hidden rounded-full font-bold text-xs uppercase tracking-[0.18em] px-7 py-4 w-full text-white transition-shadow duration-300 ${className}`}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -translate-x-[130%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover/mag:translate-x-[130%]"
        />
        {children}
      </a>
    </motion.div>
  );
};

export default function Contact() {
  const { t } = useLanguage();
  const c = t('contactPage');
  useSEO('contact');

  const rows = [
    { icon: Phone, label: c.phone, value: CONTACT.phone, href: CONTACT.phoneHref, testId: 'contact-phone' },
    { icon: Mail, label: c.email, value: CONTACT.email, href: `mailto:${CONTACT.email}`, testId: 'contact-email' },
    { icon: MapPin, label: c.address, value: CONTACT.address, href: `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.address)}`, testId: 'contact-address' },
    { icon: Clock, label: c.hours, value: CONTACT.hours, testId: 'contact-hours' },
    { icon: Facebook, label: 'Facebook', value: 'Domidoor Solutions', href: SOCIAL.facebook, testId: 'contact-facebook' },
    { icon: Instagram, label: 'Instagram', value: '@domidoor.solutions', href: SOCIAL.instagram, testId: 'contact-instagram' },
  ];

  return (
    <main data-testid="contact-page" className="max-w-[100rem] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 mb-24 md:mb-32">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <Overline className="mb-8">{c.overline}</Overline>
          </motion.div>
          <MaskedTitle
            lines={[c.title]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="text-smoke text-base max-w-lg leading-relaxed"
          >
            {c.desc}
          </motion.p>

          <div className="mt-10 md:mt-12 flex flex-col border-t border-line/10">
            {rows.map((r, i) => {
              const Icon = r.icon;
              const inner = (
                <>
                  <span className="font-mono text-sm font-light text-ink/25 sm:w-12 shrink-0">
                    {(i + 1).toString().padStart(2, '0')}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke/80 flex items-center gap-2 sm:w-36 shrink-0">
                    <Icon size={13} className="text-accent" />
                    {r.label}
                  </span>
                  <span className="font-display text-lg md:text-xl font-medium tracking-tight flex-1">
                    <RollText text={r.value} />
                  </span>
                  <ArrowUpRight
                    size={17}
                    className="text-ink/25 group-hover/roll:text-accent group-hover/roll:-translate-y-0.5 group-hover/roll:translate-x-0.5 transition-[color,transform] duration-300 shrink-0"
                  />
                </>
              );
              const rowCls =
                'group/roll relative flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-5 py-5 md:py-6 border-b border-line/10 px-3 -mx-3 sm:px-4 sm:-mx-4 hover:bg-surface2/40 transition-colors duration-500 overflow-hidden';
              return (
                <Reveal key={r.testId} delay={0.1 + i * 0.07} y={20}>
                  {r.href ? (
                    <a
                      data-testid={r.testId}
                      href={r.href}
                      target={r.href.startsWith('http') ? '_blank' : undefined}
                      rel="noreferrer"
                      className={rowCls}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={r.testId} className={rowCls.replace('cursor-pointer', '')}>
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <Reveal delay={0.2} className="lg:sticky lg:top-36 z-10">
            <div className="bg-surface/80 backdrop-blur-2xl border border-line/10 rounded-3xl p-6 sm:p-8 md:p-12 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
              <div className="flex items-center justify-between gap-4 mb-8 pb-8 border-b border-line/10">
                <p className="font-display text-2xl font-medium">{t('hero.formTitle')}</p>
                <span className="flex items-center gap-2 text-xs text-smoke">
                  <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse ring-4 ring-[#25D366]/20" />
                  {t('hero.formSub')}
                </span>
              </div>
              <LeadForm testIdPrefix="contact-lead-form" />
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal>
        <div data-testid="contact-map" className="group relative w-full h-[500px] lg:h-[600px] rounded-[2.5rem] overflow-hidden border border-line/10 shadow-[0_30px_80px_rgba(24,26,32,0.10)]">
          <iframe
            title="Domidoor Solutions — Cenad, Timiș"
            src="https://www.google.com/maps?q=Cenad,+Timi%C8%99,+Romania&output=embed"
            className="w-full h-full map-frame grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-[filter,opacity] duration-1000"
            loading="lazy"
          />
          <div className="md:absolute md:bottom-10 md:left-10 md:max-w-sm bg-paper/80 backdrop-blur-2xl border border-white/20 rounded-3xl p-7 md:p-9 shadow-[0_24px_60px_rgba(24,26,32,0.16)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-4">{c.map}</p>
            <p className="font-display text-xl font-medium leading-snug mb-2">{CONTACT.address}</p>
            <p className="text-smoke text-sm mb-6 flex items-center gap-2">
              <Clock size={14} className="text-accent" /> {CONTACT.hours}
            </p>
            <MagneticLink
              href={CONTACT.whatsapp}
              testId="contact-whatsapp-button"
              className="bg-[#25D366] shadow-[0_8px_24px_rgba(37,211,102,0.4)] hover:shadow-[0_12px_32px_rgba(37,211,102,0.55)]"
            >
              <WhatsAppIcon size={18} /> {c.whatsapp}
            </MagneticLink>
          </div>
        </div>
      </Reveal>

      <Testimonials />
    </main>
  );
}
